import { fetchJson } from '~/lib/fetch';
import type { DiscussionSort, DiscussionFilter } from './types';
import { ITEMS_PER_PAGE_ON_DEAL } from './constants';

interface SearchRequestParamsV2 {
  query: string;
  page?: number;
  skip_pages?: number;
  items_per_page?: number;
  from?: number;
}

interface ListRequestParamsV2 {
  sort: 'date' | 'helpful' | 'rating' | 'latest';
  order: 'asc' | 'desc';
  moderator_filter?: 'unapproved' | 'unresolved' | 'needs_reply';
  page: number;
  skip_pages?: number;
  items_per_page?: number;
  from?: number;
}

interface DiscussionUrlType {
  dealId: number;
  type: 'dealreview' | 'dealcomment';
  page?: number;
  sort?: DiscussionSort;
  filter?: DiscussionFilter;
  query?: string;
  paginateOnSearch?: boolean;
  itemsPerPage?: number;
  skipPages?: number;
}

/**
 * Construct the URL for fetching comments from the discussions API.
 */
export function getDiscussionsUrlV2({
  dealId,
  type,
  page = 1,
  sort = 'date',
  filter = 'all',
  query,
  paginateOnSearch = false,
  itemsPerPage = ITEMS_PER_PAGE_ON_DEAL,
  skipPages = 0,
}: DiscussionUrlType) {
  if (query) {
    const requestParams: SearchRequestParamsV2 = {
      query,
    };

    if (paginateOnSearch) {
      if (skipPages > 0) {
        requestParams.skip_pages = skipPages;
      } else {
        requestParams.from = (page - 1) * itemsPerPage;
        requestParams.items_per_page = itemsPerPage;
      }
    }

    const requestString = Object.keys(requestParams)
      .map(
        (key: string) =>
          `${key}=${encodeURIComponent(
            requestParams[key as keyof SearchRequestParamsV2] as string,
          )}`,
      )
      .join('&');
    return `/api/v2/deals/${dealId}/${
      type === 'dealreview' ? 'reviews' : 'questions'
    }/?${requestString}`;
  } else {
    const requestParams: ListRequestParamsV2 = {
      page: page,
      sort: 'date',
      order: 'desc',
    };

    if (skipPages > 0) {
      requestParams.skip_pages = skipPages;
    } else {
      requestParams.from = (page - 1) * itemsPerPage;
      requestParams.items_per_page = itemsPerPage;
    }

    switch (sort) {
      case 'latest':
        requestParams['sort'] = 'latest';
        break;

      case 'newest':
        (requestParams['sort'] = 'date'), (requestParams['order'] = 'desc');
        break;

      case 'oldest':
        (requestParams['sort'] = 'date'), (requestParams['order'] = 'asc');
        break;

      case 'helpful':
        requestParams['sort'] = 'helpful';
        break;

      case 'rating:asc':
        (requestParams['sort'] = 'rating'), (requestParams['order'] = 'asc');
        break;

      case 'rating:desc':
        (requestParams['sort'] = 'rating'), (requestParams['order'] = 'desc');
        break;
    }

    switch (filter) {
      case 'unapproved':
        requestParams['moderator_filter'] = 'unapproved';
        break;

      case 'unresolved':
        requestParams['moderator_filter'] = 'unresolved';
        break;

      case 'needs_reply':
        requestParams['moderator_filter'] = 'needs_reply';
        break;
    }

    const requestString = Object.keys(requestParams)
      .map(
        (key) =>
          `${key}=${encodeURIComponent(
            requestParams[key as keyof ListRequestParamsV2] as string,
          )}`,
      )
      .join('&');

    return `/api/v2/deals/${dealId}/${
      type === 'dealreview' ? 'reviews' : 'questions'
    }/?${requestString}`;
  }
}

/**
 * Fetch comments from the discussions API.
 */
export async function getCommentsV2(url: string) {
  return fetchJson(url).then((data) => {
    return data || {};
  });
}
